/* Core & libraries */
import { useState } from 'react';
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import { GET_COMPANYREMOVAL_ENDPOINT, COMPANYREMOVAL_ENDPOINT } from "../endpoints/endpoints";
import { useUserContext } from "../contexts/userContext";
import { COMPANY_DELETED_ROUTE } from '../constants/routes';
import { useNotificationContext } from "../contexts/notificationContext";
import eventEmitter from './eventEmitterService';
import { AIRLINES } from "../constants/appConstants";

export const useCompanyDeleteDetailsService = (aaAdminCompanyEntityId, injectedProviderCode) => {
  const { genericApiFetch } = useGenericApiFetch();
  const userDetails = useUserContext();
  const { setNotification } = useNotificationContext();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;

  // Prefer providerCode from caller; fall back to user context
  const providerCode =
    injectedProviderCode ?? userDetails?.userInfo?.providerInfo?.providerCode;

  const isAA = providerCode === AIRLINES.AA;

  const [removalDetails, setRemovalDetails] = useState(null);
  const [removalStatus, setRemovalStatus] = useState(null);

  const ensureProvider = () => {
    if (!providerCode) {
      throw new Error("providerCode is required to call company delete endpoints.");
    }
  };

  const buildUrl = (template, id) =>
    template
      .replace("{providerCode}", String(providerCode).toLowerCase())
      .replace("{companyEntityId}", id);

  const getCompanyRemovalDetails = async () => {
    // Only AA supports “removal rules”
    if (!isAA) {
      setRemovalDetails(null);
      setRemovalStatus("success");
      return;
    }

    ensureProvider();
    const id = aaAdminCompanyEntityId || companyEntityId;

    const url = buildUrl(GET_COMPANYREMOVAL_ENDPOINT, id);

    const response = await genericApiFetch({ url, method: "GET" });

    if (response.success) {
      setRemovalDetails(response.data);
      setRemovalStatus("success");
    } else {
      setRemovalStatus("error");
    }
  };

  const postCompanyRemoval = async (removalDueToFraud, fraudCategory, removeNow) => {
    ensureProvider();
    const id = aaAdminCompanyEntityId || companyEntityId;

    const url = buildUrl(COMPANYREMOVAL_ENDPOINT, id);

    // AA needs extended payload; others just need the ID
    const data = isAA
      ? { companyEntityId: id, isFraud: removalDueToFraud, fraudCategory, removeNow }
      : { companyEntityId: id };

    const response = await genericApiFetch({
      url,
      method: "POST",
      data,
      spinner: "deleteCompanyActionSpinner"
    });

    if (response.success === true && response.data.status === "REMOVED") {
      window.location.href = COMPANY_DELETED_ROUTE;
    } else if (response.success === true && response.data.status === "PENDING") {
      setNotification(
        "success",
        <FormattedMessage id="notifications.companyDeletedSuccessTitle" />,
        <FormattedMessage
          id="notifications.companyDeletedSuccessDescription"
          values={{
            atmName: userDetails?.userInfo?.firstName,
            monthDayYear: response.errors ? response?.errors[0]?.errorDescription : null
          }}
        />
      );
      eventEmitter.emit('companyDeletionPending');
      return true;
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.companyDeletedErrorTitle" />,
        <FormattedMessage id="notifications.companyDeletedErrorDescription" />
      );
      return false;
    }
  };

  const resetRemovalDetails = () => {
    setRemovalDetails(null);
    setRemovalStatus(null);
  };

  return {
    getCompanyRemovalDetails,
    postCompanyRemoval,
    removalDetails,
    removalStatus,
    resetRemovalDetails,
  };
};
